import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Container,
  Popover,
  Paper,
  Divider,
  Collapse,
  Stack
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LoginIcon from '@mui/icons-material/Login';
import CloseIcon from '@mui/icons-material/Close';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WorkIcon from '@mui/icons-material/Work';
import { Link as RouterLink } from 'react-router-dom';
import CustomButton from './common/CustomButton';
import logo from '../assets/images/logo.webp';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});
  const [mobileExpanded, setMobileExpanded] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const pages = [
    { title: 'Accueil', path: '/' },
    { title: 'Qui sommes-nous ?', path: '/qui-sommes-nous' },
    {
      title: 'Nos secteurs',
      children: [
        { title: 'L2MI', path: '/secteurs-l2mi' },
        { title: 'AIR MÉTROLOGIE', path: '/secteurs-air-metrologie' }
      ]
    },
    { title: 'Nos accréditations', path: '/accreditations' },
    { title: 'Contact', path: '/contact' }
  ];

  const menuItems = [
    { 
      text: 'Accueil', 
      path: '/' 
    },
    {
      text: 'Présentation',
      submenu: [
        { 
          text: 'Qui sommes-nous ?',
          path: '/who-we-are'
        },
        { 
          text: 'Mot du Directeur',
          path: '/mot-du-directeur'
        },
        {
          text: 'Politique qualité',
          submenu: [
            {
              text: 'Politique qualité de L2MI',
              path: '/politique-qualite-l2mi'
            },
            {
              text: 'Politique qualité de Airmétrologie',
              path: '/politique-qualite-airmetrologie'
            }
          ]
        },
      ]
    },
    {
      text: 'Nos secteurs',
      submenu: [
        {
          text: "Secteurs d'activité de L2MI",
          path: '/secteurs-l2mi'
        },
        {
          text: "Secteurs d'activité de Air Metrologie",
          path: '/secteurs-air-metrologie'
        }
      ]
    },
    {
      text: 'Nos accréditations',
      path: '/accreditations'
    },
    {
      text: 'Contactez-nous',
      path: '/contact'
    }
  ];

  const styles = {
    topBar: {
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      height: '36px',
      minHeight: '36px',
      '& .MuiToolbar-root': {
        minHeight: '36px',
        height: '36px',
      }
    },
    joinButton: {
      color: '#34495E',
      textTransform: 'none',
      fontSize: '0.875rem',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      padding: '4px 12px',
      minHeight: '28px',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(115, 192, 67, 0.08)',
        color: '#73c043',
      },
    },
    divider: {
      height: '16px',
      margin: '0 12px',
      borderColor: 'rgba(0, 0, 0, 0.12)',
      alignSelf: 'center'
    },
    appBar: {
      backgroundColor: '#FFFFFF',
      boxShadow: 'none',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
      top: '36px',
      height: '80px'
    },
    toolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.5rem 0',
      minHeight: '80px',
      height: '80px'
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    mainTitle: {
      color: '#2C3E50',
      fontWeight: 700,
      fontSize: isMobile ? '1.2rem' : '1.5rem',
      marginRight: '1rem',
    },
    subTitle: {
      color: '#34495E',
      fontSize: isMobile ? '0.9rem' : '1.1rem',
      fontWeight: 500,
    },
    menuButton: {
      color: '#004B87',
      marginRight: 2,
      '& .MuiSvgIcon-root': {
        fontSize: '2rem',
        transition: 'transform 0.3s ease',
      },
      '&:hover': {
        backgroundColor: 'rgba(115, 192, 67, 0.08)',
        '& .MuiSvgIcon-root': {
          transform: 'scale(1.1)',
          color: '#73c043',
        }
      },
    },
    navButton: {
      color: '#34495E',
      margin: '0 0.5rem',
      textTransform: 'none',
      fontWeight: 500,
      position: 'relative',
      transition: 'all 0.3s ease',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '50%',
        width: 0,
        height: '2px',
        backgroundColor: '#73c043',
        transition: 'all 0.3s ease',
        transform: 'translateX(-50%)',
      },
      '&:hover': {
        backgroundColor: 'transparent',
        color: '#73c043',
        '&::after': {
          width: '80%',
        }
      },
    },
    drawer: {
      width: 280,
    },
    submenu: {
      padding: '12px 0',
      minWidth: 300,
      marginTop: '12px',
      backgroundColor: 'rgba(255, 255, 255, 0.99)',
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(115, 192, 67, 0.12)',
      borderRadius: '12px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
      display: 'flex',
    },
    submenuContent: {
      flex: 1,
    },
    submenuCTA: {
      width: '240px',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, rgba(0, 75, 135, 0.03) 0%, rgba(26, 54, 93, 0.05) 100%)',
      marginRight: '20px',
      borderRadius: '8px',
      margin: '12px 20px 12px 0',
    },
    ctaTitle: {
      color: '#1A365D',
      fontWeight: 600,
      fontSize: '1rem',
      marginBottom: '12px',
      textAlign: 'center',
    },
    ctaDescription: {
      color: '#34495E',
      fontSize: '0.9rem',
      textAlign: 'center',
      marginBottom: '16px',
      lineHeight: 1.5,
    },
    submenuItem: {
      padding: '12px 24px',
      color: '#34495E',
      transition: 'all 0.3s ease',
      position: 'relative',
      '&:hover': {
        backgroundColor: 'rgba(115, 192, 67, 0.04)',
        color: '#73c043',
        '& .MuiTypography-root': {
          transform: 'translateX(6px)',
          color: '#73c043',
        },
      },
      '& .MuiTypography-root': {
        transition: 'all 0.3s ease',
        fontSize: '0.95rem',
        letterSpacing: '0.2px',
      },
    },
    nestedSubmenuItem: {
      padding: '10px 32px',
      color: '#34495E',
      position: 'relative',
      borderLeft: '2px solid transparent',
      marginLeft: '24px',
      '&:hover': {
        backgroundColor: 'rgba(115, 192, 67, 0.04)',
        color: '#73c043',
        borderLeft: '2px solid #73c043',
        '& .MuiTypography-root': {
          transform: 'translateX(6px)',
          color: '#73c043',
        },
      },
      '& .MuiTypography-root': {
        fontSize: '0.9rem',
        transition: 'all 0.3s ease',
        letterSpacing: '0.2px',
      },
    },
    submenuDivider: {
      margin: '8px 24px',
      backgroundColor: 'rgba(115, 192, 67, 0.06)',
    },
    menuItem: {
      color: '#34495E',
      padding: '6px 12px',
      marginLeft: '8px',
      borderRadius: '6px',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(115, 192, 67, 0.04)',
        color: '#73c043',
      },
      '& .MuiButton-endIcon': {
        transition: 'transform 0.3s ease',
        marginLeft: '4px',
      },
      '&:hover .MuiButton-endIcon': {
        transform: 'rotate(180deg)',
      },
    },
    mobileMenuItem: {
      padding: '16px 24px',
      color: '#1A365D',
      transition: 'all 0.3s ease',
      position: 'relative',
      '&:hover': {
        backgroundColor: 'rgba(115, 192, 67, 0.04)',
      },
      '& .MuiListItemText-primary': {
        fontSize: '1.1rem',
        fontWeight: 600,
        letterSpacing: '0.3px',
      },
    },
    mobileMenuItemWithSubmenu: {
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      '& .MuiSvgIcon-root': {
        transition: 'transform 0.3s ease',
      },
      '&.expanded .MuiSvgIcon-root': {
        transform: 'rotate(180deg)',
      },
    },
    mobileSubMenuItem: {
      padding: '14px 48px',
      color: '#34495E',
      backgroundColor: 'rgba(115, 192, 67, 0.02)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: '32px',
        top: '50%',
        width: '4px',
        height: '4px',
        backgroundColor: '#73c043',
        borderRadius: '50%',
        transform: 'translateY(-50%)',
      },
      '&:hover': {
        backgroundColor: 'rgba(115, 192, 67, 0.06)',
        color: '#73c043',
      },
      '& .MuiListItemText-primary': {
        fontSize: '1rem',
        fontWeight: 500,
      },
    },
    mobileMenuSection: {
      marginBottom: '16px',
      '&:not(:first-of-type)': {
        borderTop: '1px solid rgba(115, 192, 67, 0.08)',
        paddingTop: '16px',
      },
    },
    mobileFooter: {
      padding: '24px',
      borderTop: '1px solid rgba(115, 192, 67, 0.08)',
      background: '#FFFFFF',
    },
    mobileButton: {
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '1rem',
      letterSpacing: '0.5px',
    },
    mobileDrawer: {
      '& .MuiDrawer-paper': {
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out !important',
        '&.MuiDrawer-paperAnchorRight': {
          transform: 'translateX(0)',
        }
      },
    },
    mobileHeader: {
      padding: '20px 24px',
      background: 'linear-gradient(to right, #004B87, #1A365D)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      position: 'relative',
    },
    mobileCloseButton: {
      color: '#FFFFFF',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    mobileContent: {
      flex: 1,
      overflowY: 'auto',
      padding: '24px 0',
    },
    submenuCollapse: {
      transition: 'height 0.3s ease',
    },
    socialButton: {
      color: '#34495E',
      padding: '4px',
      minWidth: 'auto',
      '&:hover': {
        backgroundColor: 'rgba(44, 62, 80, 0.04)',
        color: '#2C3E50',
      },
      '& .MuiSvgIcon-root': {
        fontSize: '20px'
      }
    },
  };

  const handleMenuOpen = (event, menuId) => {
    setAnchorEl({ ...anchorEl, [menuId]: event.currentTarget });
  };

  const handleMenuClose = (menuId) => {
    setAnchorEl({ ...anchorEl, [menuId]: null });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileMenuClick = (itemText) => {
    setMobileExpanded(prev => ({
      ...prev,
      [itemText]: !prev[itemText]
    }));
  };

  const handleMenuItemClick = (path) => {
    setAnchorEl({}); // Ferme tous les sous-menus
    setMobileOpen(false); // Ferme le menu mobile si ouvert
  };

  const renderSubmenuItems = (items, level = 0, parentText) => {
    return (
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box sx={styles.submenuContent}>
          {items.map((item, index, array) => (
            <React.Fragment key={item.text}>
              {item.path ? (
                <ListItem
                  component={RouterLink}
                  to={item.path}
                  onClick={() => handleMenuItemClick(item.path)}
                  sx={{
                    ...styles.submenuItem,
                    ...(level > 0 && styles.nestedSubmenuItem),
                  }}
                  disableRipple
                >
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      fontWeight: level === 0 ? 600 : 500,
                    }}
                  />
                </ListItem>
              ) : (
                <>
                  <ListItem 
                    sx={styles.submenuItem}
                    disableRipple
                  >
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{ 
                        variant: 'subtitle2',
                        fontWeight: 600,
                        color: '#1A365D',
                      }}
                    />
                  </ListItem>
                  {item.submenu && renderSubmenuItems(item.submenu, level + 1, item.text)}
                </>
              )}
              {index < array.length - 1 && level === 0 && (
                <Divider sx={styles.submenuDivider} />
              )}
            </React.Fragment>
          ))}
        </Box>
        {level === 0 && parentText === 'Présentation' && (
          <Box sx={styles.submenuCTA}>
            <Typography sx={styles.ctaTitle}>
              Faisons connaissance
            </Typography>
            <Typography sx={styles.ctaDescription}>
              Notre équipe est à votre disposition pour répondre à vos questions.
            </Typography>
            <CustomButton
              variant="contained"
              size="small"
              component="a"
              href="/contact"
            >
              Contactez-nous
            </CustomButton>
          </Box>
        )}
      </Box>
    );
  };

  const renderMobileMenu = (items, level = 0) => {
    return items.map((item) => (
      <React.Fragment key={item.text}>
        {item.path ? (
          <ListItem
            component={RouterLink}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              ...styles.mobileMenuItem,
              ...(level > 0 && styles.mobileSubMenuItem),
            }}
          >
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: level === 0 ? 600 : 500,
              }}
            />
          </ListItem>
        ) : (
          <>
            <ListItem 
              onClick={() => handleMobileMenuClick(item.text)}
              sx={{
                ...styles.mobileMenuItem,
                ...styles.mobileMenuItemWithSubmenu,
                ...(mobileExpanded[item.text] && { '&.expanded': true }),
              }}
            >
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  color: '#1A365D',
                }}
              />
              <KeyboardArrowDownIcon 
                sx={{
                  transform: mobileExpanded[item.text] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                }}
              />
            </ListItem>
            <Collapse in={mobileExpanded[item.text]} timeout="auto" sx={styles.submenuCollapse}>
              {item.submenu && item.submenu.map((subItem) => (
                <React.Fragment key={subItem.text}>
                  {subItem.path ? (
                    <ListItem
                      component={RouterLink}
                      to={subItem.path}
                      onClick={handleDrawerToggle}
                      sx={styles.mobileSubMenuItem}
                    >
                      <ListItemText primary={subItem.text} />
                    </ListItem>
                  ) : (
                    <>
                      <ListItem 
                        onClick={() => handleMobileMenuClick(subItem.text)}
                        sx={{
                          ...styles.mobileSubMenuItem,
                          ...styles.mobileMenuItemWithSubmenu,
                        }}
                      >
                        <ListItemText primary={subItem.text} />
                        <KeyboardArrowDownIcon 
                          sx={{
                            transform: mobileExpanded[subItem.text] ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                          }}
                        />
                      </ListItem>
                      <Collapse in={mobileExpanded[subItem.text]} timeout="auto" sx={styles.submenuCollapse}>
                        {subItem.submenu && subItem.submenu.map((nestedItem) => (
                          <ListItem
                            key={nestedItem.text}
                            component={RouterLink}
                            to={nestedItem.path}
                            onClick={handleDrawerToggle}
                            sx={{
                              ...styles.mobileSubMenuItem,
                              paddingLeft: '64px',
                              '&::before': {
                                left: '48px',
                              },
                            }}
                          >
                            <ListItemText primary={nestedItem.text} />
                          </ListItem>
                        ))}
                      </Collapse>
                    </>
                  )}
                </React.Fragment>
              ))}
            </Collapse>
          </>
        )}
      </React.Fragment>
    ));
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={styles.mobileHeader}>
        <IconButton
          onClick={handleDrawerToggle}
          sx={styles.mobileCloseButton}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.mobileContent}>
        {menuItems.map((section) => (
          <Box key={section.text} sx={styles.mobileMenuSection}>
            {section.path ? (
              <ListItem
                component={RouterLink}
                to={section.path}
                onClick={handleDrawerToggle}
                sx={styles.mobileMenuItem}
              >
                <ListItemText primary={section.text} />
              </ListItem>
            ) : (
              <>
                <ListItem 
                  onClick={() => handleMobileMenuClick(section.text)}
                  sx={{
                    ...styles.mobileMenuItem,
                    ...styles.mobileMenuItemWithSubmenu,
                    ...(mobileExpanded[section.text] && { '&.expanded': true }),
                  }}
                >
                  <ListItemText 
                    primary={section.text}
                    primaryTypographyProps={{
                      color: '#1A365D',
                    }}
                  />
                  <KeyboardArrowDownIcon 
                    sx={{
                      transform: mobileExpanded[section.text] ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </ListItem>
                <Collapse in={mobileExpanded[section.text]} timeout="auto" sx={styles.submenuCollapse}>
                  {section.submenu && section.submenu.map((subItem) => (
                    <React.Fragment key={subItem.text}>
                      {subItem.path ? (
                        <ListItem
                          component={RouterLink}
                          to={subItem.path}
                          onClick={handleDrawerToggle}
                          sx={styles.mobileSubMenuItem}
                        >
                          <ListItemText primary={subItem.text} />
                        </ListItem>
                      ) : (
                        <>
                          <ListItem 
                            onClick={() => handleMobileMenuClick(subItem.text)}
                            sx={{
                              ...styles.mobileSubMenuItem,
                              ...styles.mobileMenuItemWithSubmenu,
                            }}
                          >
                            <ListItemText primary={subItem.text} />
                            <KeyboardArrowDownIcon 
                              sx={{
                                transform: mobileExpanded[subItem.text] ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </ListItem>
                          <Collapse in={mobileExpanded[subItem.text]} timeout="auto" sx={styles.submenuCollapse}>
                            {subItem.submenu && subItem.submenu.map((nestedItem) => (
                              <ListItem
                                key={nestedItem.text}
                                component={RouterLink}
                                to={nestedItem.path}
                                onClick={handleDrawerToggle}
                                sx={{
                                  ...styles.mobileSubMenuItem,
                                  paddingLeft: '64px',
                                  '&::before': {
                                    left: '48px',
                                  },
                                }}
                              >
                                <ListItemText primary={nestedItem.text} />
                              </ListItem>
                            ))}
                          </Collapse>
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </Collapse>
              </>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={styles.mobileFooter}>
        <CustomButton
          variant="contained"
          fullWidth
          sx={styles.mobileButton}
          component="a"
          href="https://portail.l2mi.fr"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<LoginIcon />}
        >
          Accéder au Portail Client
        </CustomButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Top Bar */}
      <AppBar position="fixed" elevation={0} sx={styles.topBar}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
          <Toolbar disableGutters>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={0}
              sx={{ width: '100%', height: '100%' }}
            >
              <Box>
                <IconButton
                  component="a"
                  href="https://www.linkedin.com/company/l2mi"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={styles.socialButton}
                >
                  <LinkedInIcon />
                </IconButton>
              </Box>
              <Stack direction="row" alignItems="center">
                <Button
                  component={RouterLink}
                  to="/reclamation"
                  sx={styles.joinButton}
                >
                  Réclamation
                </Button>
                <Divider orientation="vertical" flexItem sx={styles.divider} />
                <Button
                  component={RouterLink}
                  to="/carrieres"
                  sx={styles.joinButton}
                >
                  Nous rejoindre
                </Button>
              </Stack>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Header */}
      <AppBar position="fixed" sx={styles.appBar}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
          <Toolbar sx={styles.toolbar} disableGutters>
            <Box sx={styles.logoContainer}>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  textDecoration: 'none',
                  alignItems: 'center'
                }}
              >
                <img src={logo} alt="Logo" style={{ height: '60px', marginRight: '10px' }} />
              </Box>

              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  flexGrow: 1,
                  textDecoration: 'none',
                  alignItems: 'center'
                }}
              >
                <img src={logo} alt="Logo" style={{ height: '40px', marginRight: '10px' }} />
              </Box>
            </Box>

            {isMobile ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ 
                    display: { sm: 'none' },
                    ...styles.menuButton
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ 
                    display: { sm: 'none' },
                    ...styles.menuButton
                  }}
                >
                  <MenuIcon />
                </IconButton>

                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 2 }}>
                  {menuItems.map((item) => (
                    <React.Fragment key={item.text}>
                      {item.submenu ? (
                        <>
                          <Button
                            sx={styles.navButton}
                            onClick={(e) => handleMenuOpen(e, item.text)}
                            endIcon={<KeyboardArrowDownIcon />}
                          >
                            {item.text}
                          </Button>
                          <Popover
                            open={Boolean(anchorEl[item.text])}
                            anchorEl={anchorEl[item.text]}
                            onClose={() => handleMenuClose(item.text)}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            PaperProps={{
                              sx: styles.submenu,
                              elevation: 0,
                            }}
                          >
                            <Paper elevation={0} sx={{ background: 'transparent' }}>
                              {renderSubmenuItems(item.submenu, 0, item.text)}
                            </Paper>
                          </Popover>
                        </>
                      ) : (
                        <Button
                          component={RouterLink}
                          to={item.path}
                          sx={styles.navButton}
                        >
                          {item.text}
                        </Button>
                      )}
                    </React.Fragment>
                  ))}
                  <CustomButton
                    variant="contained"
                    component="a"
                    href="https://portail.airmetrologie.ma/"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<LoginIcon />}
                  >
                    Portail Client
                  </CustomButton>
                </Box>
              </Box>
            )}
          </Toolbar>
        </Container>

        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={styles.mobileDrawer}
        >
          {drawer}
        </Drawer>
      </AppBar>
    </Box>
  );
};

export default Header;
